import React, { createContext, useContext, useState, useEffect } from "react";

const STORAGE_KEY = "callup_accessibility";

const defaultState = {
  enabled: false,
  fontScale: "normal", // normal | large | xlarge
  highContrast: false,
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    return {
      enabled: !!parsed.enabled,
      fontScale: ["normal", "large", "xlarge"].includes(parsed.fontScale)
        ? parsed.fontScale
        : "normal",
      highContrast: !!parsed.highContrast,
    };
  } catch {
    return defaultState;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (_) {}
}

function applyToDocument(state) {
  const body = document.body;
  if (!body) return;
  body.classList.remove(
    "accessibility-on",
    "accessibility-large",
    "accessibility-xlarge",
    "accessibility-contrast",
  );
  if (!state.enabled) return;
  body.classList.add("accessibility-on");
  if (state.fontScale === "large") body.classList.add("accessibility-large");
  if (state.fontScale === "xlarge") body.classList.add("accessibility-xlarge");
  if (state.highContrast) body.classList.add("accessibility-contrast");
}

const AccessibilityContext = createContext(null);

export function AccessibilityProvider({ children }) {
  const [state, setState] = useState(loadState);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    applyToDocument(state);
    saveState(state);
  }, [state]);

  const setEnabled = (enabled) => setState((s) => ({ ...s, enabled }));
  const setFontScale = (fontScale) => setState((s) => ({ ...s, fontScale }));
  const setHighContrast = (highContrast) =>
    setState((s) => ({ ...s, highContrast }));

  const value = {
    ...state,
    setEnabled,
    setFontScale,
    setHighContrast,
    isPanelOpen,
    openPanel: () => setIsPanelOpen(true),
    closePanel: () => setIsPanelOpen(false),
  };

  return (
    <AccessibilityContext.Provider value={value}>
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  const ctx = useContext(AccessibilityContext);
  if (!ctx)
    throw new Error(
      "useAccessibility must be used within AccessibilityProvider",
    );
  return ctx;
}
